<!--
  ReasonPicker.vue (Phase F1.d — 2026-06-13 design bundle).

  Card: https://trello.com/c/hYlwyKYT
  Pebble: F1.d services + events.py + ReasonPicker.

  Generic shared component per the implementation plan F1.d:

  > **ReasonPicker.vue** -- generic shared component per P2-Q10. Bypass-
  > stale is the first consumer. Future cancel/force-approve actions
  > plug in. Consumes ``bypass_reasons`` lookup.

  Surface
  -------

  Props:
    - modelValue   number | null         Selected bypass_reasons.id.
    - reasons      Array<{               Reason rows from the
        id, slug, display_name           bypass_reasons API. Order
      }>                                 preserved in the dropdown.
    - required     boolean (default true) Confirm CTA stays disabled
                                          until a reason is selected.
    - placeholder  string                Dropdown empty-state label.
                                          Default: "Select a reason".
    - allowFreeform boolean (default true) When true, render a notes
                                           textarea beneath the dropdown
                                           and emit ``update:notes`` on
                                           every keystroke. Notes are
                                           freeform string -- the backend
                                           stores them on
                                           ApprovalAction.notes.
    - notes        string (default '')   Initial notes value (v-model
                                          on update:notes).
    - notesRequired boolean (default false) When true, the notes field
                                          is labeled required and the
                                          confirm CTA stays disabled until
                                          it holds non-blank text. For
                                          surfaces where the free-text
                                          WHY is part of the audit (the
                                          library's approve-on-behalf).
    - notesHelp    string (default '')   Helper line under the notes
                                          field (e.g. who can read the
                                          note); linked via
                                          aria-describedby. None if empty.
    - confirmLabel string                Label for the confirm CTA.
                                          Default: "Confirm".
    - cancelLabel  string                Label for the cancel CTA.
                                          Default: "Cancel".
    - confirmBlocked boolean (default false) Keeps the confirm CTA
                                          disabled while an input the
                                          caller renders beside the
                                          picker is unfilled (the
                                          library's client picker).

  Emits:
    - update:modelValue(reasonId | null) Fired on every dropdown change.
    - update:notes(notes)                Fired on every keystroke when
                                          allowFreeform is true.
    - confirm()                          Fired when the user clicks the
                                          confirm CTA. Parent owns the
                                          actual mutation (bypass /
                                          reject / etc.).
    - cancel()                           Fired when the user clicks the
                                          cancel CTA.

  data-testids:
    reason-picker (root)
    reason-picker-select (dropdown)
    reason-picker-option-<reason.id> (per option)
    reason-picker-notes (notes textarea)
    reason-picker-confirm (confirm CTA)
    reason-picker-cancel (cancel CTA)

  Design intent
  -------------

  The component does NOT itself fetch the bypass_reasons list -- the
  caller passes them in. Two reasons:

    1. Caller-side caching: the list is small and stable; one fetch per
       Builder workspace mount is enough. The picker is opened many
       times per session.
    2. Reuse across action types: bypass-stale, reject, future
       force-approve / cancel can each filter or relabel the reason set
       upstream without the component knowing.

  Required-by-default mirrors F-Q10 ("REQUIRED-reason picker") -- the
  bypass surface NEEDS the reason. Setters that want a non-required
  picker (e.g. a future "add an optional note" surface) pass
  ``required: false``.

  No Vuetify -- the picker is rendered as a plain ``<select>`` + ``<textarea>``
  so it can mount unmodified in vitest with @vitejs/plugin-vue (see
  ``project_vitest-no-plugin-vue.md``). The surrounding workspace
  surfaces wrap this picker in a Vuetify dialog when needed.
-->
<template>
  <div class="reason-picker" data-testid="reason-picker">
    <label class="reason-picker__label" for="reason-picker-select">
      Reason
      <span v-if="required" class="reason-picker__required" aria-hidden="true">*</span>
    </label>
    <select
      id="reason-picker-select"
      class="reason-picker__select"
      data-testid="reason-picker-select"
      :value="modelValue"
      @change="onReasonChange($event.target.value)"
    >
      <option :value="''" :disabled="required">{{ placeholder }}</option>
      <option
        v-for="r in reasons"
        :key="r.id"
        :value="r.id"
        :data-testid="`reason-picker-option-${r.id}`"
      >
        {{ r.display_name }}
      </option>
    </select>

    <div v-if="allowFreeform" class="reason-picker__notes">
      <label class="reason-picker__label" for="reason-picker-notes">
        <template v-if="notesAreRequired">
          Notes
          <span class="reason-picker__required" aria-hidden="true">*</span>
        </template>
        <template v-else>Notes (optional)</template>
      </label>
      <textarea
        id="reason-picker-notes"
        class="reason-picker__textarea"
        data-testid="reason-picker-notes"
        :value="notes"
        rows="3"
        placeholder="Add context (e.g. 'client called -- ok to bypass')"
        :aria-describedby="notesHelp ? 'reason-picker-notes-help' : null"
        @input="onNotesInput($event.target.value)"
      ></textarea>
      <p
        v-if="notesHelp"
        id="reason-picker-notes-help"
        class="reason-picker__help"
        data-testid="reason-picker-notes-help"
      >{{ notesHelp }}</p>
    </div>

    <div class="reason-picker__actions">
      <button
        type="button"
        class="reason-picker__btn reason-picker__btn--secondary"
        data-testid="reason-picker-cancel"
        @click="$emit('cancel')"
      >
        {{ cancelLabel }}
      </button>
      <button
        type="button"
        class="reason-picker__btn reason-picker__btn--primary"
        data-testid="reason-picker-confirm"
        :disabled="confirmDisabled"
        @click="$emit('confirm')"
      >
        {{ confirmLabel }}
      </button>
    </div>
  </div>
</template>

<script>
// Reasons whose note is the record of WHY (Bug Intake #731: the distributor
// override). The server enforces the same; this keeps confirm disabled
// until the note is written. Mirrors NOTES_REQUIRED_SLUGS in the API.
const NOTES_REQUIRED_SLUGS = ['distributor_override_accepts_responsibility']

export default {
  name: 'ReasonPicker',
  props: {
    modelValue: {
      type: [Number, String, null],
      default: null,
    },
    reasons: {
      type: Array,
      required: true,
      validator: (val) =>
        Array.isArray(val) &&
        val.every(
          (r) =>
            r &&
            (typeof r.id === 'number' || typeof r.id === 'string') &&
            typeof r.display_name === 'string'
        ),
    },
    required: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'Select a reason',
    },
    allowFreeform: {
      type: Boolean,
      default: true,
    },
    notes: {
      type: String,
      default: '',
    },
    notesRequired: {
      type: Boolean,
      default: false,
    },
    notesHelp: {
      type: String,
      default: '',
    },
    confirmLabel: {
      type: String,
      default: 'Confirm',
    },
    cancelLabel: {
      type: String,
      default: 'Cancel',
    },
    // The caller's own required input is not filled yet (e.g. the
    // client picker beside it): keep confirm disabled until it is.
    confirmBlocked: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:notes', 'confirm', 'cancel'],
  computed: {
    selectedReason() {
      if (this.modelValue === null || this.modelValue === undefined) return null
      return (
        this.reasons.find((r) => String(r.id) === String(this.modelValue)) ||
        null
      )
    },
    notesAreRequired() {
      return (
        this.notesRequired ||
        NOTES_REQUIRED_SLUGS.includes(this.selectedReason?.slug)
      )
    },
    confirmDisabled() {
      if (this.confirmBlocked) return true
      if (this.notesAreRequired && !String(this.notes || '').trim()) return true
      if (!this.required) return false
      // Required + no selection => disabled. '' is the placeholder
      // option's value; null is the no-selection initial state.
      return (
        this.modelValue === null ||
        this.modelValue === undefined ||
        this.modelValue === ''
      )
    },
  },
  methods: {
    onReasonChange(rawValue) {
      if (rawValue === '' || rawValue === null || rawValue === undefined) {
        this.$emit('update:modelValue', null)
        return
      }
      // Coerce numeric ids back to Number so parent state stays typed.
      const n = Number(rawValue)
      this.$emit('update:modelValue', Number.isFinite(n) ? n : rawValue)
    },
    onNotesInput(rawValue) {
      this.$emit('update:notes', rawValue)
    },
  },
}
</script>

<style scoped>
.reason-picker {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--color-surface, #ffffff);
  border-radius: 8px;
}

.reason-picker__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text, #1f2937);
}

.reason-picker__required {
  color: var(--color-danger, #dc2626);
  margin-left: 0.125rem;
}

.reason-picker__select,
.reason-picker__textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border, #d1d5db);
  border-radius: 6px;
  font-size: 0.9375rem;
  font-family: inherit;
  background: var(--color-input-bg, #ffffff);
  color: var(--color-text, #1f2937);
}

.reason-picker__textarea {
  resize: vertical;
  min-height: 4.5rem;
}

.reason-picker__help {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--color-text-muted, #6b7280);
}

.reason-picker__notes {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.reason-picker__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.reason-picker__btn {
  padding: 0.5rem 1rem;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
}

.reason-picker__btn--secondary {
  background: transparent;
  color: var(--color-text, #1f2937);
  border-color: var(--color-border, #d1d5db);
}

.reason-picker__btn--primary {
  background: var(--color-primary, #2563eb);
  color: var(--color-on-primary, #ffffff);
}

.reason-picker__btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
